import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Download,
  Share,
  Printer,
  Calendar,
  DollarSign,
  Users,
  Target,
  AlertTriangle,
  FileText,
  Settings,
  Clock,
  BarChart3,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  Shield,
  Zap,
  MapPin,
  Building,
  User,
  Briefcase,
  PieChart,
  Mail,
  Phone,
  Globe,
  Tag,
  Award,
  Activity,
  Layers,
  FolderOpen,
  Link2,
  Bell,
  Eye,
  Lock,
  Plus,
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const ViewProject = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { success, error: showError } = useToast();

  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabParam || 'overview');
  const [loading, setLoading] = useState(true);

  const [projectData, setProjectData] = useState({
    name: '',
    description: '',
    projectCode: '',
    status: 'Planning',
    priority: 'Medium',
    category: '',
    department: '',
    projectType: '',
    businessUnit: '',
    startDate: '',
    endDate: '',
    estimatedDuration: '',
    budget: 0,
    actualCost: 0,
    currency: 'USD',
    progress: 0,
    costCenter: '',
    projectManager: null,
    projectManagerName: '',
    budgetBreakdown: {},
    billingMilestones: [],
    resources: [],
    stakeholderDetails: [],
    objectives: [],
    deliverables: [],
    milestones: [],
    risks: [],
    assumptions: [],
    constraints: [],
    reportingFrequency: '',
    communicationChannels: [],
    meetingSchedule: '',
    projectTools: [],
    integrations: [],
    documentRepository: '',
    knowledgeBase: '',
    complianceRequirements: [],
    securityLevel: '',
    dataClassification: '',
    approvalWorkflow: '',
    autoNotifications: true,
    timeTracking: true,
    budgetAlerts: true,
    riskMonitoring: true
  });
  // Team collaboration assigns projects on the Team Member side (TeamMember.assignedProjects).
  // Those assignments were not previously visible in this Project view. This state holds
  // the team members that have this project in their assignedProjects list so we can
  // surface them alongside the project.team array.
  const [assignedTeamMembers, setAssignedTeamMembers] = useState([]);

  // Helper to load team members that have this project in their assignedProjects list.
  // This bridges the gap between:
  //  - Project.team (User-based assignments stored on the Project document)
  //  - TeamMember.assignedProjects (project assignments done from the Team Collaboration UI)
  const loadAssignedTeamMembers = async (projectId) => {
    try {
      // Fetch all active team members for the company; we will filter client-side
      const res = await fetch(`${API_CONFIG.ENDPOINTS.TEAM_MEMBERS}?limit=500`, {
        headers: API_CONFIG.HEADERS,
      });
      if (!res.ok) {
        throw new Error('Failed to load team members');
      }
      const json = await res.json();
      const members = json.data || json || [];

      const filtered = members.filter((member) =>
        (member.assignedProjects || []).some((p) => {
          if (!p || !p.project) return false;
          // project is stored as a string ObjectId in JSON; compare to current projectId
          return String(p.project) === String(projectId);
        })
      );
      setAssignedTeamMembers(filtered);
    } catch (err) {
      console.error('Error loading assigned team members:', err);
      // We don't block the page on this; just log and continue
    }
  };

  useEffect(() => {
    const loadProject = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_CONFIG.ENDPOINTS.PROJECTS}/${id}`, {
          headers: API_CONFIG.HEADERS,
        });

        if (response.ok) {
          const data = await response.json();
          const project = data.data?.project || data.project || {};
          console.log('Loaded project data:', project);
          console.log('Milestones:', project.milestones);
          console.log('Milestones count:', project.milestones?.length || 0);
          setProjectData(project);
          // NEW: also pull in team members that have this project assigned via Team Collaboration
          const projectId = project._id || id;
          if (projectId) {
            loadAssignedTeamMembers(projectId);
          }
        } else {
          throw new Error('Failed to load project');
        }
      } catch (err) {
        console.error('Error loading project:', err);
        showError('Failed to load project details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadProject();
    } else {
      console.error('No project ID found in URL params');
      showError('Project ID is required');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (
      tabParam &&
      [
        'overview',
        'team',
        'finance',
        'milestones',
        'risks',
        'settings',
      ].includes(tabParam)
    ) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  // Helper functions
  const formatCurrency = (amount, currencyOverride) => {
    const currency = currencyOverride || projectData.currency || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      Planning: {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200',
      },
      Active: {
        bg: 'bg-green-50',
        text: 'text-green-700',
        border: 'border-green-200',
      },
      'In Progress': {
        bg: 'bg-green-50',
        text: 'text-green-700',
        border: 'border-green-200',
      },
      'On Hold': {
        bg: 'bg-yellow-50',
        text: 'text-yellow-700',
        border: 'border-yellow-200',
      },
      Completed: {
        bg: 'bg-gray-50',
        text: 'text-gray-700',
        border: 'border-gray-200',
      },
      Cancelled: {
        bg: 'bg-red-50',
        text: 'text-red-700',
        border: 'border-red-200',
      },
    };
    return statusMap[status] || statusMap['Planning'];
  };

  const getPriorityColor = (priority) => {
    const priorityMap = {
      Low: { bg: 'bg-gray-100', text: 'text-gray-700' },
      Medium: { bg: 'bg-blue-100', text: 'text-blue-700' },
      High: { bg: 'bg-orange-100', text: 'text-orange-700' },
      Critical: { bg: 'bg-red-100', text: 'text-red-700' },
    };
    return priorityMap[priority] || priorityMap['Medium'];
  };

  const getProjectManagerName = () => {
    if (projectData.projectManagerName) return projectData.projectManagerName;
    if (
      projectData.projectManager &&
      typeof projectData.projectManager === 'object'
    ) {
      return (
        projectData.projectManager.name ||
        projectData.projectManager.fullName ||
        projectData.projectManager.email ||
        'N/A'
      );
    }
    return projectData.projectManager || 'N/A';
  };

  const renderAssetContractsSection = () => {
    const assets = projectData.assets || [];
    const contracts = projectData.contracts || [];

    const assetContent = assets.length > 0 ? (
      <div className="space-y-4">
        {assets.map((entry, index) => {
          const asset = entry.asset || {};
          const assetStatus = asset.status || 'Unknown';
          const roleLabel = entry.role || 'Role not specified';
          const assignedDate = formatDate(entry.assignedDate);
          return (
            <div key={`asset-${index}`} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-lg font-semibold text-gray-900">
                    {asset.name || asset.assetCode || 'Unnamed Asset'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {asset.type || 'Type unavailable'}
                  </p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                  {assetStatus}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500">
                <span>Role: {roleLabel}</span>
                <span>Assigned: {assignedDate}</span>
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <p className="text-sm text-gray-500 text-center py-10">No assets have been assigned to this project yet.</p>
    );

    const contractContent = contracts.length > 0 ? (
      <div className="space-y-4">
        {contracts.map((link, index) => {
          const contract = link.contract || {};
          const contractStatus = contract.status || 'Draft';
          const linkedOn = formatDate(link.linkedDate || contract.linkedDate || contract.createdAt);
          const contractAmount = contract.amount ? formatCurrency(contract.amount, contract.currency) : 'Amount not set';
          return (
            <div key={`contract-${index}`} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-lg font-semibold text-gray-900">
                    {contract.title || contract.contractId || 'Unnamed Contract'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {contract.counterparty || 'Counterparty unavailable'}
                  </p>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                  {contractStatus}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-500">
                <div>
                  <span className="font-semibold text-gray-900">{contractAmount}</span>
                  <p>Contract value</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-900">
                    {formatDate(contract.startDate)} – {formatDate(contract.endDate)}
                  </span>
                  <p>Term dates</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-900">{linkedOn}</span>
                  <p>Linked on</p>
                </div>
                <div>
                  <span className="font-semibold text-gray-900">{link.role || 'N/A'}</span>
                  <p>Assignment</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    ) : (
      <p className="text-sm text-gray-500 text-center py-10">No contracts linked to this project yet.</p>
    );

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-gray-50 rounded-lg">
              <FolderOpen className="w-6 h-6 text-gray-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Linked Assets</h2>
              <p className="text-sm text-gray-500">{assets.length} assets assigned</p>
            </div>
          </div>
          {assetContent}
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-gray-50 rounded-lg">
              <Link2 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Linked Contracts</h2>
              <p className="text-sm text-gray-500">{contracts.length} contracts</p>
            </div>
          </div>
          {contractContent}
        </div>
      </div>
    );
  };

  // Render Overview Tab
  const renderOverview = () => {
    const statusColors = getStatusColor(projectData.status);
    const priorityColors = getPriorityColor(projectData.priority);
    const budgetBreakdown = projectData.budgetBreakdown || {};
    const totalBudget = Number(projectData.budget) || 0;
    const actualSpending = Number(projectData.actualCost) || 0;
    const variance = totalBudget - actualSpending;
    const budgetUtilization =
      totalBudget > 0 ? ((actualSpending / totalBudget) * 100).toFixed(1) : 0;

    return (
      <div className="space-y-6">
        {/* Key Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">
              Total Budget
            </h3>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(totalBudget)}
            </p>
            <p className="text-xs text-gray-500 mt-2">
              {projectData.currency || 'USD'}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <Activity className="w-6 h-6 text-purple-600" />
              </div>
              <Target className="w-5 h-5 text-purple-500" />
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">Progress</h3>
            <p className="text-2xl font-bold text-gray-900">
              {projectData.progress || 0}%
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${projectData.progress || 0}%` }}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">
              Team Members
            </h3>
            <p className="text-2xl font-bold text-gray-900">
              {(projectData.team || []).length +
                (projectData.resources || []).length}
            </p>
            <p className="text-xs text-gray-500 mt-2">Active resources</p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-50 rounded-lg">
                <Target className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <h3 className="text-sm font-medium text-gray-600 mb-1">
              Milestones
            </h3>
            <p className="text-2xl font-bold text-gray-900">
              {(projectData.milestones || []).length}
            </p>
            <p className="text-xs text-gray-500 mt-2">Total milestones</p>
          </div>
        </div>

        {/* Project Header Card */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-lg p-8 text-white">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${statusColors.bg} ${statusColors.text}`}
                >
                  {projectData.status || 'Planning'}
                </span>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${priorityColors.bg} ${priorityColors.text}`}
                >
                  {projectData.priority || 'Medium'} Priority
                </span>
                {projectData.projectCode && (
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white">
                    {projectData.projectCode}
                  </span>
                )}
              </div>
              <h1 className="text-3xl font-bold mb-3">
                {projectData.name || 'Untitled Project'}
              </h1>
              <p className="text-blue-100 text-lg leading-relaxed max-w-3xl">
                {projectData.description || 'No description provided'}
              </p>
            </div>
          </div>
        </div>

        {/* Project Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Timeline & Budget */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Timeline & Budget
              </h2>
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">Start Date</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {formatDate(projectData.startDate)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <Target className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">End Date</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {formatDate(projectData.endDate)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">Duration</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {projectData.estimatedDuration || 'N/A'}
                </span>
              </div>
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <DollarSign className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    Budget Utilization
                  </span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {budgetUtilization}%
                </span>
              </div>
            </div>
          </div>

          {/* Budget Overview */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-green-50 rounded-lg">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Budget Overview
              </h2>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Budget</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(totalBudget)}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Spent</span>
                  <span className="font-medium text-gray-900">
                    {formatCurrency(actualSpending)}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-semibold">
                  <span
                    className={
                      variance >= 0 ? 'text-green-600' : 'text-red-600'
                    }
                  >
                    Remaining
                  </span>
                  <span
                    className={
                      variance >= 0 ? 'text-green-600' : 'text-red-600'
                    }
                  >
                    {formatCurrency(Math.abs(variance))}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mt-3">
                  <div
                    className={`h-3 rounded-full transition-all duration-300 ${
                      actualSpending > totalBudget
                        ? 'bg-red-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(budgetUtilization, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Objectives & Deliverables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Target className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Objectives
              </h2>
            </div>
            <ul className="space-y-3">
              {(projectData.objectives || []).length > 0 ? (
                projectData.objectives.map((objective, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      {typeof objective === 'string'
                        ? objective
                        : objective.name || JSON.stringify(objective)}
                    </span>
                  </li>
                ))
              ) : (
                <li className="text-gray-500 text-sm">No objectives defined</li>
              )}
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <Award className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Deliverables
              </h2>
            </div>
            <ul className="space-y-3">
              {(projectData.deliverables || []).length > 0 ? (
                projectData.deliverables.map((deliverable, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <FileText className="w-5 h-5 text-indigo-500 mt-0.5 shrink-0" />
                    <span className="text-gray-700">
                      {typeof deliverable === 'string'
                        ? deliverable
                        : deliverable.name || JSON.stringify(deliverable)}
                    </span>
                  </li>
                ))
              ) : (
                <li className="text-gray-500 text-sm">
                  No deliverables defined
                </li>
              )}
            </ul>
        </div>
      </div>
      
        {/* Project Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gray-50 rounded-lg">
              <Briefcase className="w-5 h-5 text-gray-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Project Information
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Category
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.category || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Department
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.department || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Business Unit
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.businessUnit || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Project Type
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.projectType || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Cost Center
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.costCenter || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Project Manager
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {getProjectManagerName()}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render Team Tab
  const renderTeam = () => {
    return (
      <div className="space-y-6">
        {/* Project Manager */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-50 rounded-lg">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Project Manager
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {getProjectManagerName()}
              </h3>
              <p className="text-sm text-gray-500">Primary Project Manager</p>
            </div>
          </div>
        </div>

        {/* Team Members (Project.team – users explicitly added to this project) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Team Members
              </h2>
            </div>
            <span className="text-sm text-gray-500">
              {(projectData.team || []).length} members (project team)
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(projectData.team || []).map((member, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900">
                      {member.user?.name || member.user?.email || 'Team Member'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {member.role || 'Team Member'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            {(projectData.team || []).length === 0 && (
              <p className="text-gray-500 text-sm col-span-full">
                No team members assigned
              </p>
            )}
          </div>
        </div>

        {/* Assigned Team Members (Team Collaboration – TeamMember.assignedProjects) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Assigned Team Members (via Team Collaboration)
              </h2>
            </div>
            <span className="text-sm text-gray-500">
              {assignedTeamMembers.length} members
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assignedTeamMembers.map((member) => {
              const primaryProject = (member.assignedProjects || []).find(
                (p) => String(p.project) === String(projectData._id || id)
              );
              return (
                <div
                  key={member._id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">
                        {member.name || member.email || 'Team Member'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {primaryProject?.role || member.position || 'Team Member'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 space-y-1 text-xs text-gray-500">
                    {member.email && <p>{member.email}</p>}
                    {member.department && <p>Dept: {member.department}</p>}
                  </div>
                </div>
              );
            })}
            {assignedTeamMembers.length === 0 && (
              <p className="text-gray-500 text-sm col-span-full">
                No team members have this project assigned from the Team Collaboration module.
              </p>
            )}
          </div>
        </div>

        {/* Resources */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Briefcase className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Resources</h2>
            </div>
            <span className="text-sm text-gray-500">
              {(projectData.resources || []).length} resources
            </span>
          </div>
          <div className="space-y-4">
            {(projectData.resources || []).map((resource, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {resource.name || 'Unnamed Resource'}
                      </h3>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          resource.type === 'employee'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}
                      >
                        {resource.type || 'N/A'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      {resource.role || 'No role specified'}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Rate:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {formatCurrency(resource.rate || 0)}/
                          {resource.currency || 'USD'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Hours:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {resource.hours || 0}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Location:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {resource.location || 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Status:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {resource.status || 'N/A'}
                        </span>
                      </div>
                    </div>
                    {resource.skills && resource.skills.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {resource.skills.map((skill, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {(projectData.resources || []).length === 0 && (
              <p className="text-gray-500 text-sm text-center py-8">
                No resources assigned
              </p>
            )}
          </div>
        </div>

        {/* Stakeholders */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Stakeholders
              </h2>
            </div>
            <span className="text-sm text-gray-500">
              {(projectData.stakeholderDetails || []).length} stakeholders
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(projectData.stakeholderDetails || []).map(
              (stakeholder, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                >
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {stakeholder.name || 'Unnamed Stakeholder'}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    {stakeholder.role || 'N/A'}
                  </p>
                  <div className="space-y-2 text-sm">
                    {stakeholder.organization && (
                      <div className="flex items-center gap-2">
                        <Building className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          {stakeholder.organization}
                        </span>
                      </div>
                    )}
                    {stakeholder.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          {stakeholder.email}
                        </span>
                      </div>
                    )}
                    {stakeholder.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">
                          {stakeholder.phone}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex gap-2">
                    {stakeholder.influence && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                        {stakeholder.influence} influence
                      </span>
                    )}
                    {stakeholder.interest && (
                      <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                        {stakeholder.interest} interest
                      </span>
                    )}
                  </div>
                </div>
              )
            )}
            {(projectData.stakeholderDetails || []).length === 0 && (
              <p className="text-gray-500 text-sm col-span-full text-center py-8">
                No stakeholders defined
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render Finance Tab
  const renderFinance = () => {
    const budgetBreakdown = projectData.budgetBreakdown || {};
    const totalBudget = Number(projectData.budget) || 0;
    const actualSpending = Number(projectData.actualCost) || 0;
    const variance = totalBudget - actualSpending;
    const variancePercent =
      totalBudget > 0 ? ((variance / totalBudget) * 100).toFixed(1) : 0;
    const breakdownTotal =
      Number(budgetBreakdown.labor || 0) +
      Number(budgetBreakdown.materials || 0) +
      Number(budgetBreakdown.equipment || 0) +
      Number(budgetBreakdown.travel || 0) +
      Number(budgetBreakdown.other || 0);

    const categories = [
      { key: 'labor', label: 'Labor Costs', color: '#3b82f6', icon: Users },
      { key: 'materials', label: 'Materials', color: '#10b981', icon: Layers },
      {
        key: 'equipment',
        label: 'Equipment',
        color: '#f59e0b',
        icon: Settings,
      },
      {
        key: 'travel',
        label: 'Travel & Expenses',
        color: '#8b5cf6',
        icon: MapPin,
      },
      {
        key: 'other',
        label: 'Other Expenses',
        color: '#ef4444',
        icon: FolderOpen,
      },
    ];

    return (
      <div className="space-y-6">
        {/* Budget Summary */}
        <div className="bg-gradient-to-r from-green-600 to-green-700 rounded-xl shadow-lg p-8 text-white">
          <h2 className="text-2xl font-bold mb-6">Budget Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-green-100 text-sm mb-2">Total Budget</p>
              <p className="text-3xl font-bold">
                {formatCurrency(totalBudget)}
              </p>
            </div>
            <div>
              <p className="text-green-100 text-sm mb-2">Actual Spending</p>
              <p className="text-3xl font-bold">
                {formatCurrency(actualSpending)}
              </p>
            </div>
            <div>
              <p className="text-green-100 text-sm mb-2">Variance</p>
              <p
                className={`text-3xl font-bold ${
                  variance >= 0 ? 'text-green-100' : 'text-red-200'
                }`}
              >
                {variance >= 0 ? '+' : ''}
                {formatCurrency(Math.abs(variance))}
              </p>
              <p className="text-green-100 text-sm mt-1">
                ({variancePercent}%)
              </p>
            </div>
          </div>
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-green-100">Budget Utilization</span>
              <span className="text-green-100 font-semibold">
                {totalBudget > 0
                  ? ((actualSpending / totalBudget) * 100).toFixed(1)
                  : 0}
                %
              </span>
            </div>
            <div className="w-full bg-green-500/30 rounded-full h-3">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${
                  actualSpending > totalBudget ? 'bg-red-300' : 'bg-white'
                }`}
                style={{
                  width: `${Math.min(
                    (actualSpending / totalBudget) * 100,
                    100
                  )}%`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Budget Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-50 rounded-lg">
              <PieChart className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Budget Breakdown by Category
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => {
              const Icon = category.icon;
              const amount = Number(budgetBreakdown[category.key] || 0);
              const percentage =
                breakdownTotal > 0
                  ? ((amount / breakdownTotal) * 100).toFixed(1)
                  : 0;

              return (
                <div
                  key={category.key}
                  className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${category.color}20` }}
                    >
                      <Icon
                        className="w-5 h-5"
                        style={{ color: category.color }}
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">
                        {category.label}
                      </h3>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {formatCurrency(amount)}
                      </p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: category.color,
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {percentage}% of breakdown
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Financial Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Shield className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Financial Controls
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Budget Approval
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.budgetApprovalRequired
                  ? 'Required'
                  : 'Not Required'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Budget Approver
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.budgetApprover || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                ROI Target
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.roiTarget || 0}%
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Variance Threshold
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.budgetVarianceThreshold || 0}%
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Expense Approval Limit
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {formatCurrency(projectData.expenseApprovalLimit || 0)}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Reporting Frequency
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.financialReporting || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Billing Milestones */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <Target className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Billing Milestones
              </h2>
            </div>
            <span className="text-sm text-gray-500">
              {(projectData.billingMilestones || []).length} milestones
            </span>
          </div>
          <div className="space-y-4">
            {(projectData.billingMilestones || []).map((milestone, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {milestone.name || 'Unnamed Milestone'}
                      </h3>
                      {milestone.autoGenerate && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                          Auto-Generate
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      {milestone.description || 'No description'}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Amount:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {formatCurrency(milestone.amount || 0)}{' '}
                          {milestone.currency || 'USD'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Billing Date:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {formatDate(milestone.billingDate)}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Trigger:</span>
                        <span className="ml-2 font-medium text-gray-900">
                          {milestone.trigger || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {(projectData.billingMilestones || []).length === 0 && (
              <p className="text-gray-500 text-sm text-center py-8">
                No billing milestones defined
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render Milestones Tab
  const renderMilestones = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Project Milestones
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">
                {(projectData.milestones || []).length} milestones
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log(
                    'Create Milestone button clicked, project ID:',
                    id
                  );
                  if (id) {
                    navigate(`/projects/${id}/milestones/create`);
                  } else {
                    console.error(
                      'Cannot create milestone: project ID is missing'
                    );
                    showError('Project ID is required to create a milestone');
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                <Plus className="w-4 h-4" />
                Create Milestone
              </button>
            </div>
          </div>
          <div className="space-y-4">
            {(projectData.milestones || []).map((milestone, index) => {
              const milestoneData =
                typeof milestone === 'object' ? milestone : { name: milestone };
              const milestoneId = milestoneData._id || milestoneData.id;
              console.log(
                `Milestone ${index}:`,
                milestoneData,
                'ID:',
                milestoneId
              );
              const status = milestoneData.status || 'pending';
              const statusColors = {
                pending: 'bg-gray-100 text-gray-700',
                'in-progress': 'bg-blue-100 text-blue-700',
                completed: 'bg-green-100 text-green-700',
                overdue: 'bg-red-100 text-red-700',
              };

              return (
                <div
                  key={milestoneId || index}
                  className="border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold">
                          {index + 1}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {milestoneData.name || 'Unnamed Milestone'}
                          </h3>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              statusColors[status] || statusColors.pending
                            }`}
                          >
                            {status}
                          </span>
                        </div>
                        {milestoneData.description && (
                          <p className="text-sm text-gray-600 mb-3">
                            {milestoneData.description}
                          </p>
                        )}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          {milestoneData.dueDate && (
                            <div>
                              <span className="text-gray-500">Due Date:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {formatDate(milestoneData.dueDate)}
                              </span>
                            </div>
                          )}
                          {milestoneData.startDate && (
                            <div>
                              <span className="text-gray-500">Start Date:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {formatDate(milestoneData.startDate)}
                              </span>
                            </div>
                          )}
                          {milestoneData.completedDate && (
                            <div>
                              <span className="text-gray-500">Completed:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {formatDate(milestoneData.completedDate)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {milestoneId && id && (
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('Edit milestone clicked:', {
                              milestoneId,
                              projectId: id,
                            });
                            if (milestoneId && id) {
                              navigate(
                                `/projects/${id}/milestones/${milestoneId}/edit`
                              );
                            } else {
                              console.error(
                                'Cannot edit milestone: missing milestoneId or projectId',
                                { milestoneId, id }
                              );
                              showError(
                                'Unable to edit milestone: missing information'
                              );
                            }
                          }}
                          className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-2"
                          title="Edit milestone"
                        >
                          <Edit size={14} />
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {(projectData.milestones || []).length === 0 && (
              <p className="text-gray-500 text-sm text-center py-8">
                No milestones defined
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render Risks Tab
  const renderRisks = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Project Risks
              </h2>
            </div>
            <span className="text-sm text-gray-500">
              {(projectData.risks || []).length} risks
            </span>
          </div>
          <div className="space-y-4">
            {(projectData.risks || []).map((risk, index) => {
              const riskData =
                typeof risk === 'object' ? risk : { title: risk };
              return (
                <div
                  key={index}
                  className="border border-red-200 rounded-lg p-5 bg-red-50/50 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {riskData.title || riskData.name || 'Unnamed Risk'}
                      </h3>
                      {riskData.description && (
                        <p className="text-sm text-gray-600 mb-3">
                          {riskData.description}
                        </p>
                      )}
                      <div className="flex flex-wrap gap-2">
                        {riskData.impact && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                            Impact: {riskData.impact}
                          </span>
                        )}
                        {riskData.probability && (
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                            Probability: {riskData.probability}
                          </span>
                        )}
                        {riskData.status && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                            Status: {riskData.status}
                          </span>
                        )}
                      </div>
                      {riskData.mitigationPlan && (
                        <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                          <p className="text-xs font-medium text-gray-500 mb-1">
                            Mitigation Plan
                          </p>
                          <p className="text-sm text-gray-700">
                            {riskData.mitigationPlan}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {(projectData.risks || []).length === 0 && (
              <p className="text-gray-500 text-sm text-center py-8">
                No risks identified
              </p>
            )}
          </div>
        </div>

        {/* Assumptions & Constraints */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-yellow-50 rounded-lg">
                <Zap className="w-5 h-5 text-yellow-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Assumptions
              </h2>
            </div>
            <ul className="space-y-3">
              {(projectData.assumptions || []).map((assumption, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">
                    {typeof assumption === 'string'
                      ? assumption
                      : JSON.stringify(assumption)}
                  </span>
                </li>
              ))}
              {(projectData.assumptions || []).length === 0 && (
                <li className="text-gray-500 text-sm">
                  No assumptions defined
                </li>
              )}
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-orange-50 rounded-lg">
                <Shield className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Constraints
              </h2>
            </div>
            <ul className="space-y-3">
              {(projectData.constraints || []).map((constraint, index) => (
                <li key={index} className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">
                    {typeof constraint === 'string'
                      ? constraint
                      : JSON.stringify(constraint)}
                  </span>
                </li>
              ))}
              {(projectData.constraints || []).length === 0 && (
                <li className="text-gray-500 text-sm">
                  No constraints defined
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Render Settings Tab
  const renderSettings = () => {
    return (
      <div className="space-y-6">
        {/* Communication & Reporting */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Communication & Reporting
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Reporting Frequency
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.reportingFrequency || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Meeting Schedule
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.meetingSchedule || 'N/A'}
              </p>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Communication Channels
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {(projectData.communicationChannels || []).map(
                  (channel, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                    >
                      {channel}
                    </span>
                  )
                )}
                {(projectData.communicationChannels || []).length === 0 && (
                  <span className="text-gray-500 text-sm">
                    No channels configured
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tools & Integrations */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Settings className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Tools & Integrations
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Project Tools
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {(projectData.projectTools || []).map((tool, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                  >
                    {tool}
                  </span>
                ))}
                {(projectData.projectTools || []).length === 0 && (
                  <span className="text-gray-500 text-sm">
                    No tools configured
                  </span>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Integrations
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {(projectData.integrations || []).map((integration, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm"
                  >
                    {integration}
                  </span>
                ))}
                {(projectData.integrations || []).length === 0 && (
                  <span className="text-gray-500 text-sm">
                    No integrations configured
                  </span>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Document Repository
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.documentRepository || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Knowledge Base
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.knowledgeBase || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Compliance & Security */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-red-50 rounded-lg">
              <Shield className="w-5 h-5 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Compliance & Security
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Compliance Requirements
              </label>
              <div className="mt-2 flex flex-wrap gap-2">
                {(projectData.complianceRequirements || []).map(
                  (req, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm"
                    >
                      {req}
                    </span>
                  )
                )}
                {(projectData.complianceRequirements || []).length === 0 && (
                  <span className="text-gray-500 text-sm">
                    No requirements specified
                  </span>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Security Level
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.securityLevel || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Data Classification
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.dataClassification || 'N/A'}
              </p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Approval Workflow
              </label>
              <p className="mt-1 text-sm font-medium text-gray-900">
                {projectData.approvalWorkflow || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Advanced Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gray-50 rounded-lg">
              <Zap className="w-5 h-5 text-gray-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Advanced Settings
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-900">
                  Auto Notifications
                </span>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  projectData.autoNotifications
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {projectData.autoNotifications ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-900">
                  Time Tracking
                </span>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  projectData.timeTracking
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {projectData.timeTracking ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-900">
                  Budget Alerts
                </span>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  projectData.budgetAlerts
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {projectData.budgetAlerts ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-900">
                  Risk Monitoring
                </span>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  projectData.riskMonitoring
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {projectData.riskMonitoring ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'team':
        return renderTeam();
      case 'finance':
        return renderFinance();
      case 'milestones':
        return renderMilestones();
      case 'risks':
        return renderRisks();
      case 'settings':
        return renderSettings();
      default:
        return renderOverview();
    }
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader
          title="View Project"
          description="Loading project details..."
        />
        <div className="page-container">
          <div
            className="flex justify-center items-center"
            style={{ height: '400px' }}
          >
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </>
    );
  }

  const statusColors = getStatusColor(projectData.status);
  const headerActions = [
  {
    label: 'Edit Project',
    icon: <Edit size={18} />,
    onClick: () => navigate(`/projects/project-management/edit/${id}`),
    variant: 'primary',
  },
  {
    label: '',
    icon: <Download size={18} />,
    onClick: () => console.log('Download project'),
    variant: 'outline',
    tooltip: 'Download project',
  },
  {
    label: '',
    icon: <Share size={18} />,
    onClick: () => console.log('Share project'),
    variant: 'outline',
    tooltip: 'Share project',
  },
];

  return (
    <>
      <PageBreadcrumb />
      <div className="page-container">
        {/* Header */}

<PageHeader
  title={projectData.name || 'View Project'}
  description={projectData.description || 'Project details'}
  actions={headerActions}
/>


        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {[
              { id: 'overview', label: 'Overview', icon: Eye },
              { id: 'team', label: 'Team', icon: Users },
              { id: 'finance', label: 'Finance', icon: DollarSign },
              { id: 'milestones', label: 'Milestones', icon: Target },
              { id: 'risks', label: 'Risks', icon: AlertTriangle },
              { id: 'settings', label: 'Settings', icon: Settings },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setSearchParams({ tab: tab.id });
                  }}
                  className={`flex items-center gap-2 px-6 py-4 font-medium text-sm transition-colors border-b-2 ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="mb-8">{renderTabContent()}</div>
      </div>
    </>
  );
};

export default ViewProject;
